import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import { Response, ErrorResponse, RequestConfig } from '../api-Utils';
import { removeAuthCookies, setAuthCookies, setUserCookies, removeUserCookies } from '../../utils/Cookie-utils';
import { clearSessionTimeoutMonitor, getAccessToken, startSessionTimeoutMonitor } from '../../utils/session-utils';
import useToggle from '../../hooks/use-toggle';

const useAuthenticationService = () => {
  const [isLoading, toggleLoading] = useToggle();

  const signIn = async (email, phoneNumber, password) => {
    try {
      toggleLoading();
      const requestBody = { password };
      if (email && email.trim() !== '') {
        requestBody.email = email;
      }
      if (phoneNumber && phoneNumber.trim() !== '') {
        requestBody.phoneNumber = phoneNumber;
      }
      
      const requestConfig = RequestConfig(
        endpoints.authentication.signIn,
        {},
        requestBody,
        'POST'
      );
      
      const response = await axiosInstance(requestConfig);
      
      if (response.status === 200) {
        const { token, refreshToken } = response.data.data;
        removeUserCookies();
        removeAuthCookies();
        setAuthCookies({ accessToken: token, refreshToken });
        const userContext = await fetchUserDetails();
        startSessionTimeoutMonitor({ resetActivity: true });
        return Response('0', response.data.message, { user: userContext });
      }
    } catch (error) {
      return ErrorResponse(error.response?.data?.message, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  const fetchUserDetails = async () => {
    try {
      toggleLoading();
      const response = await axiosInstance.get(endpoints.authentication.getContext);

      if (response?.status === 200 && response.data?.data) {
        setUserCookies(response.data.data);
        return response.data.data;
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error?.response?.data?.message || error.message);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  const signUp = async (userData) => {
    try {
      toggleLoading();
      const requestConfig = RequestConfig(
        endpoints.users.signUp,
        {},
        userData,
        'POST'
      );

      const response = await axiosInstance(requestConfig);

      if (response.status === 200) {
        return Response('0', 'User registered successfully', response.data);
      } else {
        return ErrorResponse(response.data?.message, response.status);
      }
    } catch (error) {
      return ErrorResponse(error.response?.data?.message || error.message, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  const logout = async () => {
    try {
      toggleLoading();
      const token = getAccessToken();

      if (!token) {
        throw new Error('No access token found');
      }

      const requestConfig = RequestConfig(
        endpoints.authentication.signOut,
        {},
        { token },
        'POST'
      );

      await axiosInstance(requestConfig);
      clearSessionTimeoutMonitor();
      removeAuthCookies();
      removeUserCookies();
      return Response('0', 'User logged out successfully', {});
    } catch (error) {
      return ErrorResponse(error.response?.data?.message, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  const requestPasswordResetService = async (email) => {
    try {
      toggleLoading();
      const requestConfig = RequestConfig(
        endpoints.users.resetPassword,
        {},
        { email },
        'POST'
      );

      const response = await axiosInstance(requestConfig);
      return Response('0', response.data.message, {});
    } catch (error) {
      return ErrorResponse(error.response?.data?.message, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  const verifyResetCodeService = async (email, code) => {
    try {
      toggleLoading();
      const requestConfig = RequestConfig(
        endpoints.authentication.verifyResetCode,
        {},
        { email, code },
        'POST'
      );

      const response = await axiosInstance(requestConfig);
      return Response('0', response.data.message, {});
    } catch (error) {
      return ErrorResponse(error.response?.data?.message, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  const resetPasswordService = async (email, newPassword, confirmPassword) => {
    try {
      toggleLoading();
      const requestConfig = RequestConfig(
        endpoints.users.newPassword,
        {},
        { email, password: newPassword, confirmPassword },
        'POST'
      );

      const response = await axiosInstance(requestConfig);
      return Response('0', response.data.message, {});
    } catch (error) {
      return ErrorResponse(error.response?.data?.message, error.response?.status);
    } finally {
      toggleLoading();
    }
  };

  return {
    signIn,
    signUp,
    fetchUserDetails,
    logout,
    requestPasswordResetService,
    verifyResetCodeService,
    resetPasswordService,
    isLoading,
  };
};

export default useAuthenticationService;
